import type { ComponentProps, ReactElement } from 'react';
import { Children, cloneElement, isValidElement } from 'react';

import { cn } from '../cn';
import type { ButtonProps } from './Button';
import type { ColorName } from './buttonClasses';
import type { Shape, Size } from './types';
import { shapeRounded, sizeHeight } from './types';

/** SegmentGroup sizes — `3xs` is excluded because the active segment renders one
 * size smaller and there is nothing below `3xs`. */
export type SegmentSize = Exclude<Size, '3xs'>;

// The active (and every) segment renders one step down from the group size, so
// it nestles inside the padded container as an inset pill.
const ONE_SMALLER: Record<SegmentSize, Size> = {
  '2xs': '3xs',
  xs: '2xs',
  sm: 'xs',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
  '2xl': 'xl',
  '3xl': '2xl',
  '4xl': '3xl',
};

// Pixel mirror of sizeHeight() — used to derive the container padding that frames
// the inset segments (half the height drop to the next size down).
const HEIGHT_PX: Record<Size, number> = {
  '3xs': 16,
  '2xs': 20,
  xs: 24,
  sm: 32,
  md: 36,
  lg: 40,
  xl: 48,
  '2xl': 64,
  '3xl': 80,
  '4xl': 96,
};

type SegmentGroupProps<ID> = Omit<ComponentProps<'div'>, 'onChange'> & {
  size?: SegmentSize;
  disabled?: boolean;
  shape?: Shape;
  equalWidth?: boolean;
  /** Color axis — tints the active segment's label and icon. Defaults to `'gray'`. */
  color?: ColorName;
  selectedId?: ID;
  onChange: (id: ID) => void;
};

export default function SegmentGroup<ID>({
  children,
  className,
  style,
  disabled,
  shape = 'rounded',
  equalWidth = false,
  selectedId,
  onChange,
  size = 'md',
  color = 'gray',
}: SegmentGroupProps<ID>) {
  const innerSize = ONE_SMALLER[size];
  // Frame inset = half the height drop to the size below, applied as padding +
  // gap so the active pill sits equidistant from every edge and its neighbours.
  const inset = (HEIGHT_PX[size] - HEIGHT_PX[innerSize]) / 2;

  const filtered: ReactElement<ButtonProps & { id: ID }>[] = [];
  Children.forEach(children, (child) => {
    if (child != null && isValidElement<ButtonProps & { id: ID }>(child)) {
      filtered.push(child);
    }
  });

  if (filtered.length === 0) return null;

  return (
    <div
      style={{ ...style, padding: inset, columnGap: inset }}
      className={cn(
        'inline-flex shrink-0 items-center bg-slate-100 dark:bg-slate-800',
        sizeHeight(size),
        shapeRounded(shape, size),
        className,
      )}>
      {filtered.map((child, i) => {
        const isActive = child.props.id === selectedId;
        return cloneElement(child, {
          key: i,
          size: innerSize,
          shape,
          text: true,
          // SegmentGroup owns the active styling, so never let Button paint its own.
          active: false,
          color: isActive ? color : 'gray',
          tone: isActive ? 'soft' : undefined,
          disabled: child.props.disabled ?? disabled,
          onClick: () => onChange(child.props.id),
          className: cn(
            // Force one weight for every segment: the active `soft` tone would
            // otherwise apply font-normal while inactive segments stay font-medium,
            // so labels would resize (and the group width jump) on selection.
            'h-full shrink-0 font-medium! transition-colors',
            equalWidth && 'flex-1 min-w-0',
            isActive
              ? 'bg-white! shadow-sm dark:bg-slate-700! hover:bg-white! dark:hover:bg-slate-700!'
              : 'text-slate-500! dark:text-slate-400! hover:bg-black/5! dark:hover:bg-white/5! hover:text-slate-700! dark:hover:text-slate-200!',
            child.props.className,
          ),
        });
      })}
    </div>
  );
}
